import {
  Alert,
  Button,
  Card,
  CardActions,
  CardHeader,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import {
  DataGrid,
  gridPageCountSelector,
  gridPageSelector,
  koKR,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BsArrowCounterclockwise } from "react-icons/bs";
import { apiService } from '../../api/axiosClient';
import CustomChip from '../../components/mui/chip';
import Spinner from '../../components/spinner';
import CustomNoRowsOverlay from '../../utils/CustomNoRowsOverlay';
import ConfirmDialog from '../../utils/dialog';
import SnapshotToolbar from './SnapshotToolbar';

const TableSnapshots = props => {
  const {reload, setReload} = props;
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState(0)
  const [sort, setSort] = useState('desc')
  const [pageSize, setPageSize] = useState(10)
  const [list, setList] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [sortColumn, setSortColumn] = useState('nid')
  const [selected, setSelected] = useState([])
  const [dlgopen, setDlgopen] = useState(false);

  const showDialog = () => {
    setDlgopen(true)
  }

  const handleRequest = async () => {
    setDlgopen(false)

    try {
      await apiService.put('/admin/search/snapshots');
      toast.success('Snapshot이 생성 되었습니다');
      setReload(reload + 1);
    } catch (error) {
      toast.error('Snapshot 생성이 실패했습니다');
      console.error(error);
    }
  }

  const loadSnapshots = async (sort, query, column) => {
    try {
      const response = await apiService.get('/admin/search/snapshots');
      const payload = response.data;

      setTotal(payload.size);
      setList(payload.list);
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    loadSnapshots(sort, searchValue, sortColumn)
  }, [page, pageSize, reload])

  const handleSortModel = (newModel) => {
    if (newModel.length) {
      setSort(newModel[0].sort)
      setSortColumn(newModel[0].field)
      loadSnapshots(newModel[0].sort, searchValue, newModel[0].field)
    } else {
      setSort('desc')
      setSortColumn('name')
    }
  }

  const handleSearch = value => {
    setSearchValue(value)
    loadSnapshots(sort, value, sortColumn)
  }

  const onPageSizeChange = e => {
    setPageSize(e.target.value)
  }

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const _page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Stack direction="row" spacing={2} sx={{ pr: 4, alignItems: 'center' }}>
        <InputLabel id="pagesize-select">행/페이지</InputLabel>
        <FormControl variant="standard" sx={{ mr: 1, minWidth: 70 }}>
          <Select
            labelId="pagesize-select"
            id="pagesize-select"
            value={pageSize}
            onChange={onPageSizeChange}
            label="pageSize"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
        <Pagination
          color="primary"
          count={pageCount}
          page={_page + 1}
          onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
      </Stack>
    );
  }

  const columns = [
    {
      flex: 0.2,
      minWidth: 50,
      field: 'name',
      headerName: 'Name',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.name}
        </Typography>
      )
    },
    {
      flex: 0.2,
      minWidth: 110,
      field: 'uuid',
      headerName: 'uuid',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.uuid}
        </Typography>
      )
    },
    {
      flex: 0.2,
      minWidth: 110,
      field: 'index',
      headerName: 'index',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.index}
        </Typography>
      )
    },
    {
      flex: 0.2,
      minWidth: 50,
      field: 'state',
      headerName: 'state',
      renderCell: params => (
        <CustomChip
          skin='light'
          size='small'
          label={params.row.state}
          color={'success'}
          sx={{ height: 24, fontWeight: 500, '& .MuiChip-label': { px: 1.625, lineHeight: 1.539 } }}
        />
      )
    },
    {
      flex: 0.2,
      field: 'uptime',
      minWidth: 150,
      headerName: '등록날짜',
      renderCell: params => (
        <Typography variant='body2' sx={{ color: 'text.primary' }}>
          {params.row.uptime.toLocaleString('ko-KR')}
        </Typography>
      )
    }
  ]

  return (
    loading ?
    <Spinner/>
    :
    <Card>
      <ConfirmDialog 
          open={dlgopen}
          title={'Snapshot 생성'}
          content={`Snapshot를 생성 하시겠습니까? 추후 복구에 사용될 수 있습니다.`}
          handleClose={()=>{setDlgopen(false)}}
          handleConfirm={handleRequest}
      />
      <CardHeader
        title='인덱스 스냇샵 관리'
        subheader={
          <Alert severity='info'>스냇샵 생성을 하고 해당 싯점으로 스냇샵으로 복구할 수 있습니다.</Alert>
        }
      />
      <CardActions sx={{ pt: 0, pb: 2, justifyContent: 'left' }}>
          <Tooltip title="현재 인덱싱 데이타를 저장">
            <Button variant='contained' sx={{mr:2}} onClick={showDialog}>
              Snapshot 생성
            </Button>
          </Tooltip>

          <Tooltip title="스냇샵 관리 상태 보기">
            <Button variant='contained' color='success' startIcon={<BsArrowCounterclockwise />} onClick={()=>{ setReload(reload + 1) }}>
              새로고침
            </Button>
          </Tooltip>
      </CardActions>
      <DataGrid
        sx={{
          ...(list.length == 0 && { '& .MuiDataGrid-virtualScrollerContent': {height: '200px !important' } })
        }}
        localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
        autoHeight
        getRowId={row => row.name}
        pagination
        rows={list}
        rowCount={total || 0}
        columns={columns}
        pageSize={pageSize}
        onSortModelChange={handleSortModel}
        onPageChange={newPage => setPage(newPage)}
        components={{
          Toolbar: SnapshotToolbar,
          Pagination: CustomPagination,
          NoRowsOverlay: CustomNoRowsOverlay,
        }}
        componentsProps={{
          toolbar: {
            value: searchValue,
            clearSearch: () => handleSearch(''),
            onChange: event => handleSearch(event.target.value),
            numSelected: selected.length,
            selected,
            setReload
          }
        }}
        checkboxSelection
        disableSelectionOnClick
        onSelectionModelChange={(ids) => {
          const selectedIDs = new Set(ids);
          const selectedRows = list.filter((row) =>
            selectedIDs.has(row.name),
          );

          setSelected(selectedRows)
        }}
        experimentalFeatures={{ newEditingApi: true }}
      />
    </Card>
  )
}

export default TableSnapshots